import React from 'react';
import { arrayOf, shape, string, number } from 'prop-types';
import { Box, Divider, Typography } from '@mui/material';
import { BarChart, useAxisTooltip } from '@mui/x-charts-pro';
import { Link } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';

import text from '../../../../../../localization/Application/Overview/charts';
import emptyChartIcon from '../../../../../../assets/icons/overview-chart.svg';
import top5MakesBlur from '../../../../../../assets/images/overviewGraphs/top-5-makes.png';

import { EmptyStateContainer, EmptyStateImage } from '../../../../../../global-styles/components';
import { UpgradeOverlay } from '../../../../../../components';
import CustomLegend from '../CustomLegend';
import { ColorBox, FormattedTypography, TooltipWrapper } from '../styles';

import chartUtils from '../util/chartUtils';

const staticText = text.top5MakesByConnectedVehiclesChart;

const getCustomTooltipComponent = (dataset, theme) => () => {
  const tooltipData = useAxisTooltip();

  if (!chartUtils.isTooltipDataValid(tooltipData)) {
    return null;
  }

  const datasetIndex = tooltipData.identifier.y.index;
  const totalConnections = dataset[datasetIndex].connectedVehicles;

  return (
    <TooltipWrapper>
      <FormattedTypography
        fontFamily={theme.typography.bold.fontFamily}
        marginBottom={0.5}
      >
        {staticText.tooltipTitle}
      </FormattedTypography>
      <FormattedTypography marginBottom={2}>{tooltipData.axisFormattedValue}</FormattedTypography>
      <Box display="flex" alignItems="center" marginBottom={2}>
        <ColorBox color="blue" />
        <FormattedTypography
          fontFamily={theme.typography.bold.fontFamily}
          flexGrow={1}
        >
          {tooltipData.seriesItems[0].formattedLabel}
        </FormattedTypography>
        <FormattedTypography>{tooltipData.seriesItems[0].formattedValue}</FormattedTypography>
      </Box>
      <Box display="flex" alignItems="center" marginBottom={1}>
        <ColorBox color="purple" />
        <FormattedTypography
          flexGrow={1}
          fontFamily={theme.typography.bold.fontFamily}
        >
          {tooltipData.seriesItems[1].formattedLabel}
        </FormattedTypography>
        <FormattedTypography>{tooltipData.seriesItems[1].formattedValue}</FormattedTypography>
      </Box>
      <Divider sx={{ marginTop: 2, marginBottom: 2 }} />
      <Box display="flex" alignItems="center">
        <FormattedTypography
          fontFamily={theme.typography.bold.fontFamily}
          flexGrow={1}
          marginLeft={3}
          marginRight={1}
        >
          {staticText.total}
        </FormattedTypography>
        <FormattedTypography>{totalConnections}</FormattedTypography>
      </Box>
    </TooltipWrapper>
  );
};

const Top5MakesByConnectedVehiclesChart = ({
  applicationId,
  dataset,
  maxDays,
  timeInterval,
}) => {
  const theme = useTheme();

  const legendItems = [
    {
      color: 'blue',
      label: staticText.activeLabel,
    },
    {
      color: 'purple',
      label: staticText.inactiveLabel,
    },
  ];

  const emptyState = (
    <EmptyStateContainer paperBackground condensed sx={{ flexGrow: 1 }}>
      <EmptyStateImage
        src={emptyChartIcon}
        alt=""
        sx={{ mb: theme.spacing(3) }}
      />
      <Typography variant="bold" marginBottom={1}>
        {staticText.emptyStateTitle}
      </Typography>
      <Typography variant="body2" marginBottom={3}>
        {staticText.emptyStateSubtitle}
      </Typography>
      <Link to={`/apps/${applicationId}/connect/playground`} className="button-link">
        {staticText.emptyStateButton}
      </Link>
    </EmptyStateContainer>
  );

  if (timeInterval > maxDays) {
    return (
      <Box sx={{ display: 'grid' }}>
        <UpgradeOverlay staticText={staticText.upgradeContent} />
        <img
          src={top5MakesBlur}
          alt="Horizontal bar graph showing the top 5 makes by connected vehicles"
          style={{
            width: '100%',
            gridRow: 1,
            gridColumn: 1,
          }}
        />
      </Box>
    );
  }

  if (!dataset.length) {
    return emptyState;
  }

  return (
    <Box display="flex" flexDirection="column">
      <BarChart
        dataset={dataset}
        xAxis={[{ tickNumber: 5, label: staticText.xAxisLabel }]}
        yAxis={[{
          scaleType: 'band',
          dataKey: 'make',
          label: staticText.yAxisLabel,
          categoryGapRatio: 0.4,
        }]}
        series={[
          {
            dataKey: 'activeConnections',
            color: theme.palette.charts.blue.fill,
            label: staticText.activeLabel,
            highlightScope: {
              highlighted: 'none',
              faded: 'global',
            },
            stack: 'total',
            stackOrder: 'reverse',
          },
          {
            dataKey: 'inactiveConnections',
            color: theme.palette.charts.purple.fill,
            label: staticText.inactiveLabel,
            highlightScope: {
              highlighted: 'none',
              faded: 'global',
            },
            stack: 'total',
          },
        ]}
        layout="horizontal"
        borderRadius={3}
        axisHighlight={{ x: 'none', y: 'none' }}
        grid={{ vertical: true }}
        slotProps={chartUtils.commonChartOptions.slotProps}
        tooltip={{ trigger: 'axis' }}
        slots={{
          axisContent: getCustomTooltipComponent(dataset, theme),
        }}
        margin={{
          right: 16, left: 120, top: 24,
        }}
        height={330}
        sx={{
          '& .MuiBarElement-series-auto-generated-id-0': {
            stroke: theme.palette.charts.blue.stroke,
          },
          '& .MuiBarElement-series-auto-generated-id-1': {
            stroke: theme.palette.charts.purple.stroke,
          },
          '& .MuiChartsAxis-directionY .MuiChartsAxis-label': {
            transform: `translateX(-${theme.spacing(9.5)})`,
          },
        }}
      />
      <CustomLegend legendItems={legendItems} />
    </Box>
  );
};

export default Top5MakesByConnectedVehiclesChart;

Top5MakesByConnectedVehiclesChart.propTypes = {
  applicationId: string.isRequired,
  dataset: arrayOf(shape({
    make: string.isRequired,
    connectedVehicles: number.isRequired,
    activeVehicles: number.isRequired,
  })).isRequired,
  maxDays: number.isRequired,
  timeInterval: number.isRequired,
};
